import React, { useEffect, useState } from "react";
import { Accordion } from "../../../../app/components";
import { DATA_TABLE_NAME, INITIAL_RISK_ASSESSMENT_COLORS } from "../constants";
import TableHeader from "./table.header";
import TableRow from "./table.row";
import styled from "styled-components";
import { useScheduleEvents } from "../../../../app/contexts/schedule.events.context";
import { useUser } from "../../../../app/contexts/user.context";
import { useRestoreTableVisibility } from "../hooks/useRestoreTableVisibility";
import { INITIAL_RISK_WIDTHS } from "./table.utils";
import CustomTooltip from "../../../../app/components/tooltip/custom.tooltip";

export default function CloneStudyPlan() {
  const {
    initialRiskAssessments,
    initialRiskAssessmentTotal,
    setInitialRiskAssessmentTotal,
  } = useScheduleEvents();
  const { isReadOnlyUser } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const total = initialRiskAssessments.reduce(
      (acc, curr) => acc + (+curr.riskAssessmentRiskScore || 0),
      0,
    );
    setInitialRiskAssessmentTotal((prev) => ({
      ...prev,
      total: total.toFixed(2),
    }));
    // eslint-disable-next-line
  }, [initialRiskAssessments]);

  useRestoreTableVisibility('InitialRiskAssessmentTable', setIsExpanded);

  const renderRows = () => {
    return initialRiskAssessments.map((initialRiskAssessment, index) => {
      return <TableRow
        key={initialRiskAssessment.scheduleOfEventsRiskAssessmentId || `risk-assessment-${index}`}
        data={initialRiskAssessment}
        readOnly={isReadOnlyUser}
      />;
    });
  };

  const getColor = (total) => {
    if (total > 26) {
      return INITIAL_RISK_ASSESSMENT_COLORS.RED;
    } else if (total > 18 && total <= 26) {
      return INITIAL_RISK_ASSESSMENT_COLORS.YELLOW;
    } else {
      return INITIAL_RISK_ASSESSMENT_COLORS.GREEN;
    }
  };

  const renderTotalRow = () => {
    return (
      <Row>
        <Cell
          width={INITIAL_RISK_WIDTHS.category}
          style={{ borderBottomLeftRadius: "8px" }}
        ></Cell>
        <Cell width={INITIAL_RISK_WIDTHS.studySpecifics}>{initialRiskAssessmentTotal.label}</Cell>
        <Cell
          width={INITIAL_RISK_WIDTHS.riskScore}
          style={{ background: getColor(initialRiskAssessmentTotal.total) }}
        >
          <CustomTooltip title={`Initial Risk Score: ${initialRiskAssessmentTotal.total}`}>
            <span>{initialRiskAssessmentTotal.total}</span>
          </CustomTooltip>
        </Cell>
        <Cell
          width={INITIAL_RISK_WIDTHS.lowMediumHigh}
          style={{ borderBottomRightRadius: "8px" }}
        ></Cell>
      </Row>
    );
  };

  const handleAccordionChange = (event, expanded) => {
    setIsExpanded(expanded);
  };

  return (
    <Accordion
      scrollIntoView={true}
      width={"100%"}
      summary={"Initial Risk Assessment"}
      data-table-name={DATA_TABLE_NAME.INITIAL_RISK_ASSESSMENT_TABLE}
      defaultExpanded={isExpanded}
      onChange={handleAccordionChange}
    >
      {isExpanded && (
        <Container 
          data-table-name={DATA_TABLE_NAME.INITIAL_RISK_ASSESSMENT_TABLE}
          data-table-container="true"
        >
          <TableHeader />
          {renderRows()}
          {renderTotalRow()}
        </Container>
      )}
    </Accordion>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-size: 1rem;
  border: 1px solid var(--surface-300);
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
`;

export const Row = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  min-height: 50px;
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--surface-300);
  }
  
  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

export const Cell = styled.div`
  background: ${(p) =>
    p.variant === "header" ? "var(--surface-100)" : "white"};
  min-height: 50px;
  align-items: ${({ align }) => align || "center"};
  justify-content: ${({ justify }) => justify || "center"};
  display: flex;
  flex-direction: column;
  width: ${(p) => (p.width ? p.width : "100%")};
  padding: 8px 4px;
  text-align: ${({ textAlign }) => textAlign || "center"};
  box-sizing: border-box;
  position: relative;
  
  /* Right border for all cells except last */
  &:not(:last-child) {
    border-right: 1px solid var(--surface-300);
  }
  
  /* Left border when specified */
  ${(p) => p.leftBorder && `
    border-left: 1px solid var(--surface-300);
  `}
  
  /* Right border when specifically needed on last cell */
  ${(p) => p.rightBorder && `
    border-right: 1px solid var(--surface-300);
  `}
  
  /* Full border override when needed */
  ${(p) => p.fullBorder && `
    border: 1px solid var(--surface-300);
  `}
`;
